<template>
    <BaseCard
        class="generic-card"
        v-if="entity || entityFromWorktree || isFetching"
        :entity="
            entity || entityFromWorktree || { identity: entityId, name: '' }
        "
        :icon="icon"
        :tags="tags"
        :domainName="domain?.name"
        :productName="product?.name"
        :processName="process?.name"
        :taskName="task?.name"
        :roleName="role?.name"
        :businessEntityName="businessEntity?.name"
        :isFetching="isFetching && !entityFromWorktree"
        :showPath="Boolean(showPath && entity)"
        :entityType="entityType"
        :entityRefs="entityRefs"
        :generatesEvidence="generatesEvidence"
        :evidenceInstructions="evidenceInstructions"
        :documentCount="documentCount"
    />
</template>

<script setup lang="ts">
import "@shoelace-style/shoelace/dist/components/card/card.js";
import "@shoelace-style/shoelace/dist/components/skeleton/skeleton.js";
import "@shoelace-style/shoelace/dist/themes/dark.css";
import "@shoelace-style/shoelace/dist/themes/light.css";

import { computed } from "vue";

import { useWorktreeStore } from "@/composables/worktree-manager";
import { EntityType, useEntityManager } from "@/stores/entity-store";

import BaseCard from "./BaseCard.vue";

const props = withDefaults(
    defineProps<{
        entityId: string;
        entityType: EntityType;
        showPath?: boolean;
        entityRefs?: Array<{
            entity_id: string;
            label: string;
            entityName: string;
        }>;
        generatesEvidence?: boolean;
        evidenceInstructions?: string;
        documentCount?: number;
        borderColor?: string | null;
    }>(),
    {
        showPath: true,
    }
);

const {
    isFetching,
    entity,
    businessEntity,
    task,
    process,
    product,
    domain,
    role,
} = useEntityManager(props.entityType, props.entityId);

const { findDomainByIdentity, findProductByIdentity, findProcessByIdentity } =
    useWorktreeStore();

/**
 * Fetch entity from worktree if we can, since that's already available.
 */
const entityFromWorktree = computed(() => {
    if (props.entityType === EntityType.DOMAIN) {
        return findDomainByIdentity(props.entityId);
    } else if (props.entityType === EntityType.PRODUCT) {
        return findProductByIdentity(props.entityId);
    } else if (props.entityType === EntityType.PROCESS) {
        return findProcessByIdentity(props.entityId);
    }
});

const icon = computed<string | string[] | undefined>(() => {
    if (props.entityType === EntityType.DOMAIN) {
        return "pi pi-cloud";
    } else if (props.entityType === EntityType.PRODUCT) {
        return "pi pi-box";
    } else if (props.entityType === EntityType.PROCESS) {
        return "pi pi-share-alt";
    } else if (props.entityType === EntityType.ROLE) {
        return "pi pi-id-card";
    } else if (props.entityType === EntityType.BUSINESS_ENTITY) {
        return "pi pi-building";
    } else if (props.entityType === EntityType.TASK) {
        return "pi pi-check-square";
    }
});

const processesText = (count: number) => {
    if (count === 0) {
        return "No processes";
    } else if (count === 1) {
        return `${count} process`;
    } else {
        return `${count} processes`;
    }
};

const productsText = (count: number) => {
    if (count === 0) {
        return "No products";
    } else if (count === 1) {
        return `${count} product`;
    } else {
        return `${count} products`;
    }
};

const borderLeft = computed(() =>
    props.borderColor ? `.5rem solid ${props.borderColor}` : "none"
);

type Tags = InstanceType<typeof BaseCard>["tags"];

const tags = computed<Tags>(() => {
    let processesCount: number | undefined;
    let productsCount: number | undefined;

    if (props.entityType === EntityType.PRODUCT) {
        processesCount = product.value?.processes.length || 0;
    } else if (props.entityType === EntityType.DOMAIN) {
        productsCount = domain.value?.products.length || 0;
        processesCount =
            domain.value?.products.reduce(
                (total, product) => total + product.processes.length,
                0
            ) || 0;
    }
    return [
        productsCount === undefined || {
            label: productsText(productsCount),
            severity: "primary",
        },
        processesCount === undefined || {
            label: processesText(processesCount),
            severity: "secondary",
        },
    ].filter((val) => val !== true) as Tags;
});
</script>

<style lang="scss" scoped>
.generic-card {
    --border-width: 1px;
    border-left: v-bind(borderLeft);
}
</style>
